import { useLocation } from 'react-router-dom';
import {
  Sparkles,
  CheckCircle,
  Zap,
  Shield,
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  Warehouse,
  FileText,
  Truck,
  CreditCard,
  Clock,
  UserCircle,
  MapPin,
  History,
  Star,
  Tag,
  Boxes,
  Gift,
  Handshake,
  LineChart,
  type LucideIcon,
} from 'lucide-react';
import { useFeatureIntro } from '@/hooks/useFeatureIntro';
import { FeatureIntroLayout } from './FeatureIntroLayout';
import { FeatureBenefit } from './FeatureBenefit';

// Icon map for dynamic resolution
const iconMap: Record<string, LucideIcon> = {
  Sparkles,
  CheckCircle,
  Zap,
  Shield,
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  Warehouse,
  FileText,
  Truck,
  CreditCard,
  Clock,
  UserCircle,
  MapPin,
  History,
  Star,
  Tag,
  Boxes,
  Gift,
  Handshake,
  LineChart,
};

// Icon resolver - converts string icon name to Lucide component
function getIcon(iconName: string): LucideIcon {
  return iconMap[iconName] || Sparkles;
}

// Default fallback content when no data from Control Plane
const defaultContent = {
  title: 'Coming Soon',
  subtitle: 'This feature will be available once your workspace is fully set up. Our team is working to get everything ready for you.',
  icon: Sparkles,
  contactEmail: 'support@zaviago.com',
  benefits: [
    {
      icon: CheckCircle,
      title: 'Full Access',
      description: 'Complete access to all features and functionality.',
    },
    {
      icon: Zap,
      title: 'Real-time Updates',
      description: 'See changes instantly across your entire workspace.',
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with automatic backups.',
    },
    {
      icon: BarChart3,
      title: 'Insights & Reports',
      description: 'Detailed analytics to help you make better decisions.',
    },
  ],
};

/**
 * Dynamic feature intro page.
 * Fetches content from Control Plane based on current route.
 * Falls back to default content if none configured.
 */
export function DynamicFeatureIntro() {
  const location = useLocation();
  const { data, isLoading } = useFeatureIntro(location.pathname);

  // Loading state - show minimal skeleton
  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-700 border-t-gray-400 dark:border-t-gray-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Use Control Plane data or fallback
  if (data) {
    const Icon = getIcon(data.icon);
    return (
      <FeatureIntroLayout
        icon={Icon}
        title={data.title}
        subtitle={data.subtitle}
        contactEmail={data.contact_email}
      >
        {data.benefits.map((benefit, index) => (
          <FeatureBenefit
            key={index}
            icon={getIcon(benefit.icon)}
            title={benefit.title}
            description={benefit.description}
          />
        ))}
      </FeatureIntroLayout>
    );
  }

  // Default fallback
  return (
    <FeatureIntroLayout
      icon={defaultContent.icon}
      title={defaultContent.title}
      subtitle={defaultContent.subtitle}
      contactEmail={defaultContent.contactEmail}
    >
      {defaultContent.benefits.map((benefit, index) => (
        <FeatureBenefit
          key={index}
          icon={benefit.icon}
          title={benefit.title}
          description={benefit.description}
        />
      ))}
    </FeatureIntroLayout>
  );
}
